import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ExternalLink, FileText, Mail, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import {
  useBulkPresences,
  useCreateEvaluationsForSession,
  useFormation,
  useFormateurs,
  useInscriptions,
  usePresences,
  useSession,
  useUpsertPresence,
} from '@/hooks/queries/useFormation';
import { useTiers } from '@/hooks/queries/useTiers';
import { formatDateDZ, formatDA } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { InscriptionStatut } from '@/lib/database.types';
import { generateEmargementPDF } from '@/lib/emargement-pdf';

type Tab = 'details' | 'inscrits' | 'presences' | 'evaluations';

export function SessionDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: session } = useSession(id ?? null);
  const { data: formation } = useFormation(session?.formation_id ?? null);
  const { data: insc } = useInscriptions({ sessionId: id ?? null, pageSize: 100 });
  const { data: tiers = [] } = useTiers({
    entrepriseId: formation?.entreprise_id ?? null,
    search: '',
  });
  const { data: presences = [] } = usePresences(id ?? null);
  const { data: formateurs = [] } = useFormateurs();
  const upsertPres = useUpsertPresence();

  const [tab, setTab] = useState<Tab>('details');
  const [evalModalOpen, setEvalModalOpen] = useState(false);
  const [evalType, setEvalType] = useState<'chaud' | 'froid'>('chaud');
  const bulkPresences = useBulkPresences();
  const createEvals = useCreateEvaluationsForSession();

  const tierMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of tiers) m.set(tt.id, tt.raison_sociale);
    return m;
  }, [tiers]);

  // Tous les hooks AVANT tout return conditionnel (rules-of-hooks)
  const jours = useMemo(() => {
    if (!session) return [];
    const list: string[] = [];
    const start = new Date(session.date_debut);
    const end = new Date(session.date_fin);
    for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 86400000)) {
      list.push(d.toISOString().slice(0, 10));
    }
    return list;
  }, [session]);

  const presenceMap = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const p of presences as { inscription_id: string; jour: string; present: boolean }[]) {
      m.set(`${p.inscription_id}|${p.jour}`, p.present);
    }
    return m;
  }, [presences]);

  if (!session) {
    return (
      <div className="p-6">
        <Link
          to="/formation/sessions"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
        >
          <ChevronLeft className="h-3 w-3" /> Retour
        </Link>
        <div className="mt-4 text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  const inscriptions = insc?.rows ?? [];
  const formateur = formateurs.find((f) => f.id === session.formateur_principal_id);

  const togglePresence = async (
    inscriptionId: string,
    jour: string,
    present: boolean,
  ): Promise<void> => {
    if (!id) return;
    await upsertPres.mutateAsync({
      inscription_id: inscriptionId,
      session_id: id,
      jour,
      present,
    });
  };

  const handleGenerateEmargement = (jour: string): void => {
    if (!session || !formation) return;
    const records = inscriptions
      .filter((i) => i.statut !== 'annule' && i.statut !== 'liste_attente')
      .map((i) => ({
        nom: tierMap.get(i.tier_id) ?? i.reference,
        present: presenceMap.get(`${i.id}|${jour}`) === true,
      }));
    generateEmargementPDF({
      sessionRef: session.reference,
      formationTitre: formation.titre,
      jour,
      formateurNom: formateur ? `${formateur.prenom} ${formateur.nom}` : '—',
      lieu: session.lieu_adresse ?? '—',
      participants: records,
    });
    toast.success(`Émargement PDF généré pour le ${formatDateDZ(jour)}`);
  };

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/formation/sessions"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
      >
        <ChevronLeft className="h-3 w-3" /> Retour aux sessions
      </Link>
      <PageHeader
        title={`${session.reference} — ${formation?.titre ?? ''}`}
        subtitle={
          <span className="text-xs text-muted-foreground">
            {formatDateDZ(session.date_debut)} → {formatDateDZ(session.date_fin)} ·{' '}
            <span
              className={cn(
                'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                session.type === 'intra' && 'border-violet-300 bg-violet-50 text-violet-800',
                session.type === 'inter' && 'border-indigo-300 bg-indigo-50 text-indigo-800',
                session.type === 'b2c' && 'border-slate-300 bg-slate-50 text-slate-700',
              )}
            >
              {t(`formation.session.type.${session.type}`)}
            </span>
            <Badge variant="outline" className="ml-1">
              {t(`formation.session.statut.${session.statut}`)}
            </Badge>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEvalModalOpen(true)}
              disabled={createEvals.isPending}
            >
              <Mail className="h-3.5 w-3.5" /> Créer évaluations
            </Button>
          </div>
        }
      />

      <nav className="flex flex-wrap items-center gap-1 border-b border-border">
        {(
          [
            ['details', 'Détails'],
            ['inscrits', `Inscrits (${inscriptions.length})`],
            ['presences', 'Présences'],
            ['evaluations', 'Évaluations'],
          ] as [Tab, string][]
        ).map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              'border-b-2 px-3 py-2 text-xs',
              tab === k
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {l}
          </button>
        ))}
      </nav>

      {tab === 'details' && (
        <section className="rounded-lg border border-border bg-card p-4 text-xs">
          <div className="grid grid-cols-2 gap-4">
            <Row
              label="Capacité"
              value={`${inscriptions.filter((i) => ['confirme', 'paye_partiel', 'paye_total'].includes(i.statut)).length} / ${session.capacite_max}`}
            />
            <Row label="Lieu" value={`${session.lieu_type} — ${session.lieu_adresse ?? '—'}`} />
            <Row
              label="Formateur"
              value={formateur ? `${formateur.prenom} ${formateur.nom}` : '—'}
            />
            <Row
              label="Prix session"
              value={session.prix_session ? formatDA(Number(session.prix_session)) : '—'}
            />
            <Row label="Horaires" value={session.horaires ?? '—'} />
            <Row
              label="Co-animateurs"
              value={session.formateurs_co_animateurs_ids.length.toString()}
            />
          </div>
        </section>
      )}

      {tab === 'inscrits' && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Users className="h-4 w-4" /> Liste des inscrits
          </h3>
          {inscriptions.length === 0 ? (
            <p className="text-xs text-muted-foreground">Aucun inscrit pour cette session.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="py-1">Réf</th>
                  <th className="py-1">Tiers</th>
                  <th className="py-1">Statut</th>
                  <th className="py-1">Source</th>
                  <th className="py-1 text-right">Prix</th>
                </tr>
              </thead>
              <tbody>
                {inscriptions.map((i) => (
                  <tr key={i.id} className="border-b border-border/40">
                    <td className="py-1 font-mono">{i.reference}</td>
                    <td className="py-1">{tierMap.get(i.tier_id) ?? '—'}</td>
                    <td className="py-1">
                      <Badge variant={statutVariant(i.statut)}>
                        {t(`formation.inscription.statut.${i.statut}`)}
                      </Badge>
                    </td>
                    <td className="py-1">
                      {i.opportunite_id ? (
                        <Link
                          to={`/crm/opportunite/${i.opportunite_id}`}
                          className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 hover:bg-blue-100"
                          title="Ouvrir l'opportunité CRM"
                        >
                          <ExternalLink className="h-2.5 w-2.5" /> CRM
                        </Link>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">
                          {i.source ?? 'manuelle'}
                        </span>
                      )}
                    </td>
                    <td className="num py-1 text-right">{formatDA(Number(i.prix_facture))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {tab === 'presences' && (
        <section className="rounded-lg border border-border bg-card p-4">
          <header className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <Users className="h-4 w-4" /> Présences par jour ({jours.length} jour
              {jours.length > 1 ? 's' : ''})
            </h3>
            <div className="flex items-center gap-1">
              {jours.map((j) => (
                <Button
                  key={j}
                  size="sm"
                  variant="outline"
                  onClick={() => handleGenerateEmargement(j)}
                  className="h-7 text-[10px]"
                >
                  <FileText className="h-3 w-3" /> PDF {formatDateDZ(j)}
                </Button>
              ))}
            </div>
          </header>
          {id && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {jours.map((j) => {
                const inscriptionIds = inscriptions
                  .filter((i) => i.statut !== 'annule' && i.statut !== 'liste_attente')
                  .map((i) => i.id);
                return (
                  <div
                    key={`bulk-${j}`}
                    className="flex items-center gap-1 rounded border border-border bg-muted/30 px-2 py-1"
                  >
                    <span className="text-[10px] font-medium text-muted-foreground">
                      {formatDateDZ(j)} :
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px]"
                      disabled={bulkPresences.isPending}
                      onClick={async () => {
                        const r = await bulkPresences.mutateAsync({
                          session_id: id,
                          jour: j,
                          inscription_ids: inscriptionIds,
                          present: true,
                        });
                        toast.success(`${r.success} marqué(s) présent(s)`);
                      }}
                    >
                      ✓ Tout présent
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px]"
                      disabled={bulkPresences.isPending}
                      onClick={async () => {
                        const r = await bulkPresences.mutateAsync({
                          session_id: id,
                          jour: j,
                          inscription_ids: inscriptionIds,
                          present: false,
                        });
                        toast.success(`${r.success} marqué(s) absent(s)`);
                      }}
                    >
                      ✗ Tout absent
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="py-1">Participant</th>
                  {jours.map((j) => (
                    <th key={j} className="py-1 text-center">
                      {formatDateDZ(j)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inscriptions
                  .filter((i) => i.statut !== 'annule' && i.statut !== 'liste_attente')
                  .map((i) => (
                    <tr key={i.id} className="border-b border-border/40">
                      <td className="py-1">{tierMap.get(i.tier_id) ?? i.reference}</td>
                      {jours.map((j) => {
                        const key = `${i.id}|${j}`;
                        const present = presenceMap.get(key);
                        return (
                          <td key={j} className="py-1 text-center">
                            <input
                              type="checkbox"
                              checked={!!present}
                              onChange={(e) => void togglePresence(i.id, j, e.target.checked)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'evaluations' && (
        <section className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">
            Voir l&apos;onglet Évaluations dédié pour la liste agrégée par session avec graphiques
            NPS.{' '}
            <Link to="/formation/evaluations" className="text-primary underline">
              Voir les évaluations →
            </Link>
          </p>
        </section>
      )}

      {evalModalOpen && id && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4"
          onClick={() => setEvalModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-3 text-base font-semibold">Créer évaluations pour la session</h3>
            <p className="mb-4 text-xs text-muted-foreground">
              Génère un lien public unique <code>/eval/&lt;token&gt;</code> pour chaque participant
              actif (statut ≠ annulé / liste d&apos;attente).
            </p>
            <label className="mb-3 block text-xs">
              <span className="mb-1 block font-medium">Type</span>
              <select
                value={evalType}
                onChange={(e) => setEvalType(e.target.value as 'chaud' | 'froid')}
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="chaud">À chaud (fin de session)</option>
                <option value="froid">À froid (3 mois après)</option>
              </select>
            </label>
            <div className="flex flex-col gap-2">
              <Button
                onClick={async () => {
                  const r = await createEvals.mutateAsync({
                    session_id: id,
                    type: evalType,
                    envoyer: true,
                  });
                  if (r.errors.length === 0)
                    toast.success(`${r.success} évaluation(s) créée(s) — liens actifs 7 jours`);
                  else toast.warning(`${r.success} OK · ${r.errors.length} échec(s)`);
                  setEvalModalOpen(false);
                }}
                disabled={createEvals.isPending}
              >
                <Mail className="h-3.5 w-3.5" /> Créer + envoyer (lien actif)
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  const r = await createEvals.mutateAsync({
                    session_id: id,
                    type: evalType,
                    envoyer: false,
                  });
                  if (r.errors.length === 0)
                    toast.success(
                      `${r.success} évaluation(s) créée(s) en brouillon (à activer plus tard)`,
                    );
                  else toast.warning(`${r.success} OK · ${r.errors.length} échec(s)`);
                  setEvalModalOpen(false);
                }}
                disabled={createEvals.isPending}
              >
                Créer en brouillon
              </Button>
              <Button variant="ghost" onClick={() => setEvalModalOpen(false)}>
                Annuler
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function statutVariant(
  s: InscriptionStatut,
): 'default' | 'success' | 'warning' | 'outline' | 'secondary' | 'destructive' {
  if (s === 'paye_total') return 'success';
  if (s === 'paye_partiel') return 'warning';
  if (s === 'confirme') return 'default';
  if (s === 'annule') return 'destructive';
  if (s === 'liste_attente') return 'secondary';
  return 'outline';
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
